import { Badge, Button, confirm } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  CreditCardIcon,
  DownloadIcon,
  EyeIcon,
  PaperclipIcon,
  RotateCcwIcon,
  SendIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import type {
  PayoutLineDTO,
  PayoutRecipientDTO,
  UpdatePayoutDTO,
} from "../../domain/contracts";
import { performanceApi } from "../api/client";
import { PayoutBankAccountCard } from "../ui/payout-detail/PayoutBankAccountCard";
import {
  PayoutRecipientBankEditDialog,
  type PayoutBankFormValues,
} from "../ui/PayoutRecipientBankEditDialog";
import { PerformanceBrandPrintMenu } from "../ui/PerformanceBrandPrintMenu";
import {
  useApprovePayout,
  useCancelPayout,
  useDeletePayout,
  useMarkPayoutPaid,
  usePayoutQuery,
  useRejectPayout,
  useSetPayoutPaymentProof,
  useSubmitPayout,
  useUpdatePayout,
} from "../api/queries";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

type LineForm = {
  id?: string;
  assignmentId?: string | null;
  performerId?: string | null;
  eventStaffId?: string | null;
  description: string;
  quantity: number;
  unit: string | null;
  unitAmount: number;
  adjustmentAmount: number;
  notes: string | null;
  sortOrder: number;
};

type RecipientForm = {
  id?: string;
  performerId?: string | null;
  performerName?: string | null;
  eventStaffId?: string | null;
  staffName?: string | null;
  staffRoleLabel?: string | null;
  bankName: string;
  bankAccount: string;
  accountName: string;
  subtotalNet: number;
};

function recipientKey(recipient: RecipientForm) {
  return recipient.eventStaffId ?? recipient.performerId ?? recipient.id ?? "";
}

function recipientDisplayName(recipient: RecipientForm) {
  return (
    recipient.staffName ??
    recipient.performerName ??
    recipient.eventStaffId ??
    recipient.performerId ??
    "—"
  );
}

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

function isPdfFileKey(key: string): boolean {
  return key.toLowerCase().endsWith(".pdf");
}

function isImageFileKey(key: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(key);
}

function toLineForm(line: PayoutLineDTO): LineForm {
  return {
    id: line.id,
    assignmentId: line.assignmentId,
    performerId: line.performerId,
    eventStaffId: line.eventStaffId,
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unitAmount: line.unitAmount,
    adjustmentAmount: line.adjustmentAmount,
    notes: line.notes,
    sortOrder: line.sortOrder,
  };
}

function toRecipientForm(recipient: PayoutRecipientDTO): RecipientForm {
  return {
    id: recipient.id,
    performerId: recipient.performerId,
    performerName: recipient.performerName,
    eventStaffId: recipient.eventStaffId,
    staffName: recipient.staffName,
    staffRoleLabel: recipient.staffRoleLabel,
    bankName: recipient.bankName ?? "",
    bankAccount: recipient.bankAccount ?? "",
    accountName: recipient.accountName ?? "",
    subtotalNet: recipient.subtotalNet,
  };
}

function extractPayoutRoleLabel(
  description: string,
  performerName?: string | null,
): string {
  const text = description.trim();
  if (!text) return "—";

  const separator = " - ";
  const idx = text.indexOf(separator);
  if (idx >= 0) {
    const role = text.slice(idx + separator.length).trim();
    if (role) return role;
  }

  if (performerName && text === performerName.trim()) return "—";
  return text;
}

function calculateNet(line: LineForm) {
  return line.quantity * line.unitAmount;
}

async function confirmPayoutAction({
  title,
  description,
  actionText,
  destructive,
}: {
  title: string;
  description: string;
  actionText: string;
  destructive?: boolean;
}) {
  return confirm({
    title,
    description,
    actionText,
    ActionProps: destructive ? { variant: "destructive" } : undefined,
  });
}

function formatBankAccount(input: {
  bankName?: string | null;
  bankAccount?: string | null;
  accountName?: string | null;
}) {
  return {
    bankName: input.bankName ?? null,
    bankAccount: input.bankAccount ?? null,
    accountName: input.accountName ?? null,
  };
}

type BankEditTarget =
  | {
      scope: "recipient";
      key: string;
      payeeName: string;
      hint?: string;
    }
  | {
      scope: "legacy";
      payeeName: string;
      hint?: string;
    };

function PerformerRoleDisplay({ name, role }: { name: string; role: string }) {
  return (
    <div>
      <div className="font-medium">{name}</div>
      {role && role !== "—" ? (
        <div className="text-muted-foreground text-xs">{role}</div>
      ) : null}
    </div>
  );
}

function PayoutPaymentProofPanel({
  payout,
  paymentProofUrl,
  canMarkPaid,
  onUploadProof,
}: {
  payout: {
    id: string;
    status: string;
    paymentProofFileId: string | null;
  };
  paymentProofUrl: string | null;
  canMarkPaid: boolean;
  onUploadProof: (key: string) => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-sm">
            <PaperclipIcon className="size-4" />
            ຫຼັກຖານການຈ່າຍ
          </div>
          <p className="text-muted-foreground text-sm">
            {payout.paymentProofFileId
              ? "ມີສລິບແລ້ວ — ບັນທຶກຜູກກັບໃບເບີກຈ່າຍແລ້ວ"
              : "ແນບສລິບກ່ອນບັນທຶກຈ່າຍ ຫຼື ບັນທຶກຈ່າຍໂດຍບໍ່ມີຫຼັກຖານກໍໄດ້"}
          </p>
        </div>
        {payout.paymentProofFileId && paymentProofUrl ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(paymentProofUrl, "_blank")}
            >
              <EyeIcon className="size-4" /> ເບິ່ງ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(paymentProofUrl, "_blank")}
            >
              <DownloadIcon className="size-4" /> ດາວໂຫຼດ
            </Button>
          </div>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 space-y-4">
        {payout.paymentProofFileId && paymentProofUrl ? (
          <div className="overflow-hidden rounded-lg border bg-muted/20">
            {isPdfFileKey(payout.paymentProofFileId) ? (
              <iframe
                title="Payment proof preview"
                src={paymentProofUrl}
                className="h-[min(420px,50vh)] w-full bg-white"
              />
            ) : isImageFileKey(payout.paymentProofFileId) ? (
              <img
                src={paymentProofUrl}
                alt="Payment proof"
                className="mx-auto max-h-[min(420px,50vh)] w-full bg-white object-contain"
              />
            ) : (
              <div className="flex h-40 items-center justify-center text-muted-foreground text-sm">
                ບໍ່ສາມາດ preview ຮູບແບບນີ້ໄດ້ — ໃຊ້ປຸ່ມ ເບິ່ງ / ດາວໂຫຼດ
              </div>
            )}
          </div>
        ) : (
          <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed bg-muted/10 px-4 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີສລິບໂອນ
          </div>
        )}
        {payout.status === "approved" && canMarkPaid ? (
          <PresignedFileUpload
            keyPrefix={`uploads/performance-payouts/${payout.id}/proof`}
            accept="image/*,.pdf"
            buttonLabel={
              payout.paymentProofFileId ? "ອັບໂຫຼດສລິບໃໝ່" : "ອັບໂຫຼດສລິບ"
            }
            hint="ຮອງຮັບຮູບ ຫຼື PDF; ຈະບັນທຶກຜູກກັບໃບເບີກຈ່າຍທັນທີຫຼັງອັບໂຫຼດ"
            onSuccess={onUploadProof}
          />
        ) : null}
      </div>
    </div>
  );
}

function PayoutLinesTable({
  lines,
  editable,
  onChange,
  recipientNames,
  legacyPerformerName,
  bankDetailsByRecipient,
  legacyBankDetails,
  onEditBank,
  isStaffPayout = false,
  isProductionPayout = false,
  isOutsourcePayout = false,
}: {
  lines: LineForm[];
  editable: boolean;
  onChange: (lines: LineForm[]) => void;
  recipientNames?: Record<string, string>;
  legacyPerformerName?: string;
  bankDetailsByRecipient?: Record<
    string,
    {
      bankName?: string | null;
      bankAccount?: string | null;
      accountName?: string | null;
    }
  >;
  legacyBankDetails?: {
    bankName?: string | null;
    bankAccount?: string | null;
    accountName?: string | null;
  };
  onEditBank?: (target: {
    recipientKey: string;
    payeeName: string;
    scope: "recipient" | "legacy";
  }) => void;
  isStaffPayout?: boolean;
  isProductionPayout?: boolean;
  isOutsourcePayout?: boolean;
}) {
  const isVendorPayout = isProductionPayout || isOutsourcePayout;
  const showBankColumn =
    !isVendorPayout && !!(bankDetailsByRecipient || legacyBankDetails);

  function lineRecipientKey(line: LineForm) {
    return line.eventStaffId ?? line.performerId ?? "";
  }

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {isVendorPayout
                ? isOutsourcePayout
                  ? "Outsource"
                  : "ປະເພດ"
                : isStaffPayout
                  ? "Staff"
                  : "ນັກສະແດງ"}
            </th>
            <th className="min-w-[240px] px-5 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {isVendorPayout
                ? "ລາຍລະອຽດ"
                : showBankColumn
                  ? "ບັນຊີ"
                  : "ລາຍລະອຽດ"}
            </th>
            <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ຈຳນວນ
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ໜ່ວຍ
            </th>
            <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ລາຄາ
            </th>
            <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ສຸດທິ
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {lines.map((line, index) => {
            const recipientId = lineRecipientKey(line);
            const recipientName =
              (recipientId && recipientNames?.[recipientId]) ||
              legacyPerformerName ||
              recipientId ||
              "—";
            const bankDetails =
              (recipientId && bankDetailsByRecipient?.[recipientId]) ||
              legacyBankDetails ||
              formatBankAccount({});

            return (
              <tr key={line.id ?? `${recipientId}-${index}`}>
                <td className="px-4 py-3">
                  {isVendorPayout ? (
                    <span className="text-sm">
                      {line.description || line.notes || "—"}
                    </span>
                  ) : (
                    <PerformerRoleDisplay
                      name={recipientName}
                      role={extractPayoutRoleLabel(
                        line.description,
                        recipientName,
                      )}
                    />
                  )}
                </td>
                <td className="min-w-[240px] max-w-md whitespace-normal px-5 py-3 align-top">
                  {showBankColumn ? (
                    <PayoutBankAccountCard
                      bank={bankDetails}
                      editDisabled={!editable}
                      onEdit={
                        onEditBank
                          ? () =>
                              onEditBank({
                                recipientKey: recipientId || "__legacy__",
                                payeeName: recipientName,
                                scope: recipientId ? "recipient" : "legacy",
                              })
                          : undefined
                      }
                    />
                  ) : (
                    <input
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                      value={line.description}
                      disabled={!editable}
                      onChange={(event) =>
                        onChange(
                          lines.map((item, i) =>
                            i === index
                              ? { ...item, description: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  <input
                    className="h-9 w-20 rounded-md border bg-background px-3 text-right text-sm"
                    type="number"
                    min={1}
                    value={line.quantity}
                    disabled={!editable}
                    onChange={(event) =>
                      onChange(
                        lines.map((item, i) =>
                          i === index
                            ? { ...item, quantity: Number(event.target.value) }
                            : item,
                        ),
                      )
                    }
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    className="h-9 w-24 rounded-md border bg-background px-3 text-sm"
                    value={line.unit ?? ""}
                    disabled={!editable}
                    onChange={(event) =>
                      onChange(
                        lines.map((item, i) =>
                          i === index
                            ? { ...item, unit: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    className="h-9 w-28 rounded-md border bg-background px-3 text-right text-sm"
                    type="number"
                    min={0}
                    value={line.unitAmount}
                    disabled={!editable}
                    onChange={(event) =>
                      onChange(
                        lines.map((item, i) =>
                          i === index
                            ? {
                                ...item,
                                unitAmount: Number(event.target.value),
                              }
                            : item,
                        ),
                      )
                    }
                  />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                  {formatMoney(calculateNet(line))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PayoutDetailPage() {
  const { id } = useParams({ from: "/app/performance/payouts/$id" });
  const nav = useNavigate({ from: "/app/performance/payouts/$id" });
  const payoutQuery = usePayoutQuery(id);
  const payout = payoutQuery.data;
  const canCreate = useActionPermission(["performance-payouts:create"]);
  const canApprove = useActionPermission(["performance-payouts:approve"]);
  const canMarkPaid = useActionPermission(["performance-payouts:mark-paid"]);
  const canDelete = useActionPermission(["performance-payouts:delete"]);
  const updatePayout = useUpdatePayout(id, payout?.eventId);
  const submitPayout = useSubmitPayout();
  const approvePayout = useApprovePayout();
  const rejectPayout = useRejectPayout();
  const markPaid = useMarkPayoutPaid();
  const setPaymentProof = useSetPayoutPaymentProof(id);
  const cancelPayout = useCancelPayout();
  const deletePayout = useDeletePayout(payout?.eventId ?? "");
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [accountName, setAccountName] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineForm[]>([]);
  const [recipients, setRecipients] = useState<RecipientForm[]>([]);
  const [bankEditTarget, setBankEditTarget] = useState<BankEditTarget | null>(
    null,
  );

  const paymentProofUrl = useMemo(
    () =>
      payout?.paymentProofFileId
        ? performanceApi.payouts.paymentProofUrl(payout.id)
        : null,
    [payout?.id, payout?.paymentProofFileId],
  );

  useEffect(() => {
    if (!payout) return;
    setBankName(payout.bankName ?? "");
    setBankAccount(payout.bankAccount ?? "");
    setAccountName(payout.accountName ?? "");
    setNotes(payout.notes ?? "");
    setLines((payout.lines ?? []).map(toLineForm));
    setRecipients((payout.recipients ?? []).map(toRecipientForm));
    setBankEditTarget(null);
  }, [payout]);

  const editable = payout?.status === "draft" && canCreate;
  const formTotal = useMemo(
    () => lines.reduce((sum, line) => sum + calculateNet(line), 0),
    [lines],
  );

  function updateRecipient(key: string, patch: Partial<RecipientForm>) {
    setRecipients((items) =>
      items.map((item) =>
        recipientKey(item) === key ? { ...item, ...patch } : item,
      ),
    );
  }

  const bankEditInitial = useMemo((): PayoutBankFormValues => {
    if (!bankEditTarget) {
      return { bankName: "", bankAccount: "", accountName: "" };
    }
    if (bankEditTarget.scope === "legacy") {
      return { bankName, bankAccount, accountName };
    }
    const recipient = recipients.find(
      (r) => recipientKey(r) === bankEditTarget.key,
    );
    return {
      bankName: recipient?.bankName ?? "",
      bankAccount: recipient?.bankAccount ?? "",
      accountName: recipient?.accountName ?? "",
    };
  }, [bankEditTarget, bankName, bankAccount, accountName, recipients]);

  function applyBankEdit(values: PayoutBankFormValues) {
    if (!bankEditTarget) return;
    if (bankEditTarget.scope === "legacy") {
      setBankName(values.bankName);
      setBankAccount(values.bankAccount);
      setAccountName(values.accountName);
      return;
    }
    updateRecipient(bankEditTarget.key, values);
  }

  function openTableBankEdit(target: {
    recipientKey: string;
    payeeName: string;
    scope: "recipient" | "legacy";
  }) {
    setBankEditTarget({
      scope: target.scope,
      ...(target.scope === "recipient"
        ? { key: target.recipientKey, payeeName: target.payeeName }
        : { payeeName: target.payeeName }),
      hint: payout?.isStaffPayout
        ? "ດຶງຈາກຂໍ້ມູນ Staff — ແກ້ໄຂໃຊ້ກັບໃບເບີກຈ່າຍນີ້ເທົ່ານັ້ນ"
        : "ດຶງຈາກໂປຣໄຟລ໌ — ແກ້ໄຂໃຊ້ກັບໃບເບີກຈ່າຍນີ້ເທົ່ານັ້ນ",
    });
  }

  const recipientNames = useMemo(
    () =>
      Object.fromEntries(
        recipients.map((recipient) => [
          recipientKey(recipient),
          recipientDisplayName(recipient),
        ]),
      ),
    [recipients],
  );

  const bankDetailsByRecipient = useMemo(
    () =>
      Object.fromEntries(
        recipients.map((recipient) => [
          recipientKey(recipient),
          formatBankAccount(recipient),
        ]),
      ),
    [recipients],
  );

  const legacyBankDetails = formatBankAccount({
    bankName,
    bankAccount,
    accountName,
  });

  const sortedLines = useMemo(
    () => [...lines].sort((a, b) => a.sortOrder - b.sortOrder),
    [lines],
  );

  async function save() {
    if (!payout) return;

    const input: UpdatePayoutDTO = {
      notes: notes || null,
      lines: lines.map((line, index) => ({
        id: line.id,
        assignmentId: line.assignmentId ?? null,
        performerId: line.performerId ?? null,
        eventStaffId: line.eventStaffId ?? null,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit || null,
        unitAmount: line.unitAmount,
        adjustmentAmount: 0,
        withholdingTax: 0,
        notes: line.notes || null,
        sortOrder: line.sortOrder || (index + 1) * 10,
      })),
    };

    const isVendorPayout =
      payout.isProductionPayout || payout.isOutsourcePayout;

    if (payout.isBatch) {
      if (isVendorPayout) {
        input.bankName = bankName || null;
        input.bankAccount = bankAccount || null;
        input.accountName = accountName || null;
      } else {
        input.recipients = recipients.map((recipient) => ({
          id: recipient.id,
          performerId: recipient.performerId ?? undefined,
          eventStaffId: recipient.eventStaffId ?? undefined,
          bankName: recipient.bankName || null,
          bankAccount: recipient.bankAccount || null,
          accountName: recipient.accountName || null,
        }));
      }
    } else {
      input.bankName = bankName || null;
      input.bankAccount = bankAccount || null;
      input.accountName = accountName || null;
    }

    if (isVendorPayout) {
      delete input.lines;
    }

    await updatePayout.mutateAsync(input);
  }

  if (payoutQuery.isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ໃບເບີກຈ່າຍ", to: "/app/performance/payouts" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (!payout) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ໃບເບີກຈ່າຍ", to: "/app/performance/payouts" },
              { label: "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບໃບເບີກຈ່າຍ</p>
        </Main>
      </>
    );
  }

  const isVendorPayout =
    payout.isProductionPayout || payout.isOutsourcePayout;

  const subtitle = payout.isProductionPayout
    ? `Prop / ວັດສະດຸ · ${payout.eventTitle ?? payout.eventId}`
    : payout.isOutsourcePayout
      ? `Outsource (KOL/Media) · ${payout.eventTitle ?? payout.eventId}`
    : payout.isBatch
      ? payout.isStaffPayout
        ? `${payout.recipientCount ?? recipients.length} Staff · ${payout.eventTitle ?? payout.eventId}`
        : `${payout.performerCount ?? recipients.length} ຄົນ · ${payout.eventTitle ?? payout.eventId}`
      : `${payout.performerName ?? payout.performerId} · ${payout.eventTitle ?? payout.eventId}`;

  const showPaymentProofPanel =
    payout.status === "approved" || Boolean(payout.paymentProofFileId);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບເບີກຈ່າຍ", to: "/app/performance/payouts" },
            { label: payout.payoutNo },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-bold text-3xl tracking-tight">
                {payout.payoutNo}
              </h1>
              <Badge variant={STATUS_VARIANTS[payout.status] ?? "secondary"}>
                {STATUS_LABELS[payout.status] ?? payout.status}
              </Badge>
            </div>
            <p className="mt-1 text-muted-foreground text-sm">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/performance/payouts" })}
            >
              <ArrowLeftIcon className="size-4" /> ກັບຄືນ
            </Button>
            {(isVendorPayout
              ? payout.status !== "cancelled"
              : payout.status === "approved" || payout.status === "paid") && (
              <PerformanceBrandPrintMenu
                preview={
                  isVendorPayout &&
                  (payout.status === "draft" ||
                    payout.status === "pending_approval")
                }
                buildPrintUrl={(lang) =>
                  performanceApi.payouts.printUrl(payout.id, lang)
                }
              />
            )}
            {payout.status === "draft" && canCreate && (
              <>
                <Button
                  variant="outline"
                  onClick={() => save()}
                  disabled={updatePayout.isPending}
                >
                  ບັນທຶກ
                </Button>
                <Button
                  onClick={async () => {
                    const ok = await confirmPayoutAction({
                      title: "ສົ່ງອະນຸມັດໃບເບີກຈ່າຍ",
                      description:
                        "ຫຼັງຈາກສົ່ງອະນຸມັດ ຈະແກ້ໄຂລາຍການໄດ້ອີກເມື່ອຖືກສົ່ງກັບເປັນຮ່າງ",
                      actionText: "ສົ່ງອະນຸມັດ",
                    });
                    if (!ok) return;
                    await save();
                    submitPayout.mutate(payout.id);
                  }}
                  disabled={
                    submitPayout.isPending ||
                    updatePayout.isPending ||
                    lines.length === 0
                  }
                >
                  <SendIcon className="size-4" /> ສົ່ງອະນຸມັດ
                </Button>
              </>
            )}
            {payout.status === "pending_approval" && canApprove && (
              <>
                <Button
                  variant="outline"
                  onClick={async () => {
                    const ok = await confirmPayoutAction({
                      title: "ສົ່ງກັບໄປແກ້ໄຂ",
                      description: "ໃບເບີກຈ່າຍນີ້ຈະກັບໄປສະຖານະຮ່າງ ເພື່ອໃຫ້ແກ້ໄຂລາຍການໄດ້",
                      actionText: "ສົ່ງກັບ",
                    });
                    if (ok) rejectPayout.mutate(payout.id);
                  }}
                  disabled={rejectPayout.isPending}
                >
                  <RotateCcwIcon className="size-4" /> ສົ່ງກັບ
                </Button>
                <Button
                  onClick={async () => {
                    const ok = await confirmPayoutAction({
                      title: "ອະນຸມັດໃບເບີກຈ່າຍ",
                      description: "ຫຼັງອະນຸມັດ ໃບນີ້ຈະພ້ອມບັນທຶກການຈ່າຍ",
                      actionText: "ອະນຸມັດ",
                    });
                    if (ok) approvePayout.mutate(payout.id);
                  }}
                  disabled={approvePayout.isPending}
                >
                  <CheckIcon className="size-4" /> ອະນຸມັດ
                </Button>
              </>
            )}
            {payout.status === "approved" && canMarkPaid && (
              <Button
                onClick={async () => {
                  const ok = await confirmPayoutAction({
                    title: "ບັນທຶກການຈ່າຍ",
                    description: payout.paymentProofFileId
                      ? "ຈະບັນທຶກໃບນີ້ເປັນຈ່າຍແລ້ວ ພ້ອມຫຼັກຖານການຈ່າຍທີ່ແນບໄວ້"
                      : "ຈະບັນທຶກໃບນີ້ເປັນຈ່າຍແລ້ວໂດຍຍັງບໍ່ແນບຫຼັກຖານໂອນ",
                    actionText: "ບັນທຶກຈ່າຍແລ້ວ",
                  });
                  if (ok) markPaid.mutate({ payoutId: payout.id });
                }}
                disabled={markPaid.isPending}
              >
                <CreditCardIcon className="size-4" /> ບັນທຶກຈ່າຍແລ້ວ
              </Button>
            )}
            {payout.paymentProofFileId && (
              <Button
                variant="outline"
                onClick={() =>
                  window.open(
                    performanceApi.payouts.paymentProofUrl(payout.id),
                    "_blank",
                  )
                }
              >
                <DownloadIcon className="size-4" /> Slip
              </Button>
            )}
            {payout.status !== "paid" &&
              payout.status !== "cancelled" &&
              canCreate && (
                <Button
                  variant="destructive"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "ຍົກເລີກໃບເບີກຈ່າຍ",
                      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກໃບເບີກຈ່າຍນີ້?",
                      actionText: "ຍົກເລີກ",
                      ActionProps: { variant: "destructive" },
                    });
                    if (ok)
                      cancelPayout.mutate({
                        payoutId: payout.id,
                        reason: "ຍົກເລີກຈາກໜ້າລາຍລະອຽດ",
                      });
                  }}
                  disabled={cancelPayout.isPending}
                >
                  <XIcon className="size-4" /> ຍົກເລີກ
                </Button>
              )}
            {payout.status === "cancelled" && canDelete && (
              <Button
                variant="destructive"
                onClick={async () => {
                  const ok = await confirm({
                    title: "ລຶບໃບເບີກຈ່າຍ",
                    description:
                      "ໃບເບີກຈ່າຍທີ່ຍົກເລີກແລ້ວຈະຖືກລຶບຖາວອນ — ກູ້ຄືນບໍ່ໄດ້",
                    actionText: "ລຶບ",
                    ActionProps: { variant: "destructive" },
                  });
                  if (!ok) return;
                  await deletePayout.mutateAsync(payout.id);
                  nav({
                    to: "/app/performance/events/$id",
                    params: { id: payout.eventId },
                  });
                }}
                disabled={deletePayout.isPending}
              >
                <Trash2Icon className="size-4" /> ລຶບ
              </Button>
            )}
          </div>
        </div>

        {isVendorPayout && (
          <div className="mb-6 max-w-lg">
            <h2 className="mb-3 font-semibold text-sm">ບັນຊີຮັບເງິນ (ລວມໃບ)</h2>
            <PayoutBankAccountCard
              bank={formatBankAccount({ bankName, bankAccount, accountName })}
              editDisabled={!editable}
              onEdit={
                editable
                  ? () =>
                      setBankEditTarget({
                        scope: "legacy",
                        payeeName:
                          payout.isOutsourcePayout || payout.isProductionPayout
                            ? (payout.eventTitle ?? "Vendor")
                            : (payout.performerName ?? payout.performerId ?? "—"),
                        hint: "ແກ້ໄຂໃຊ້ກັບໃບເບີກຈ່າຍນີ້ເທົ່ານັ້ນ",
                      })
                  : undefined
              }
            />
          </div>
        )}

        {showPaymentProofPanel ? (
          <div className="mb-6 max-w-2xl">
            <PayoutPaymentProofPanel
              payout={payout}
              paymentProofUrl={paymentProofUrl}
              canMarkPaid={!!canMarkPaid}
              onUploadProof={(key) =>
                setPaymentProof.mutate({ paymentProofFileId: key })
              }
            />
          </div>
        ) : null}

        {payout.isBatch ? (
          <div className="space-y-6">
            <PayoutLinesTable
              lines={sortedLines}
              editable={!!editable && !isVendorPayout}
              onChange={setLines}
              recipientNames={recipientNames}
              bankDetailsByRecipient={bankDetailsByRecipient}
              onEditBank={openTableBankEdit}
              isStaffPayout={payout.isStaffPayout}
              isProductionPayout={payout.isProductionPayout}
              isOutsourcePayout={payout.isOutsourcePayout}
            />
          </div>
        ) : (
          <>
            <PayoutLinesTable
              lines={sortedLines}
              editable={!!editable}
              onChange={setLines}
              legacyPerformerName={
                payout.performerName ?? payout.performerId ?? "—"
              }
              legacyBankDetails={legacyBankDetails}
              onEditBank={openTableBankEdit}
            />
          </>
        )}

        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_280px]">
          <label className="space-y-1.5">
            <span className="font-medium text-sm">ໝາຍເຫດ</span>
            <textarea
              className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={notes}
              disabled={!editable}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">ຍອດຈາກຟອມ</span>
              <span className="font-semibold">{formatMoney(formTotal)}</span>
            </div>
            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <span className="font-semibold">ຍອດບັນທຶກ</span>
              <span className="font-bold text-lg">
                {formatMoney(payout.totalAmount)}
              </span>
            </div>
          </div>
        </div>

        <PayoutRecipientBankEditDialog
          open={bankEditTarget !== null}
          onOpenChange={(open) => {
            if (!open) setBankEditTarget(null);
          }}
          payeeName={bankEditTarget?.payeeName ?? "—"}
          hint={bankEditTarget?.hint}
          initial={bankEditInitial}
          readOnly={!editable}
          onSave={applyBankEdit}
        />
      </Main>
    </>
  );
}
